/**
 * Provides permission and access control utilities based on user types and countries.
 *
 * ## User Types and Permissions
 *
 * ### PRAD
 * - Can invite other users within their own country.
 * - Currently has access to all features.
 *
 * ### ADAM
 * - **Anagrafiche**
 *   - Clients: read/write (anyone)
 *   - Dealers: read/write (anyone)
 *   - Price lists: read-only
 *   - Rental types: read-only
 *   - Articles: read-only
 * - **Prestazioni**
 *   - Offers: read-only (offers page) lui non può modificare le offerte, può vedere solo lettura.
 *   - Orders: read-only
 * - **Administration**: read/write
 * - **Warehouse**: read-only
 * - **Dashboard**: read-only (to be confirmed)
 * - **Cleaning**: read-only
 *
 * ### ADCO
 * - **Anagrafiche**
 *   - Clients: read/write (anyone)
 *   - Dealers: read/write (anyone)
 *   - Price lists: read-only
 *   - Rental types: read-only
 *   - Articles: read/write
 * - **Prestazioni**
 *   - Offers: read/write lui vedrà lo switch per vedere entrambe le visualizzazioni, solo lettura e write
 *   - Orders: read/write
 * - **Administration**: read-only
 * - **Dashboard**: read-only
 * - **Cleaning**: read/write
 *
 * ### COCA & COOP
 * - **Anagrafiche**
 *   - Clients: read/write (only those created by or associated with the user by ADCO)
 *   - Dealers: no access
 *   - Price lists: read-only (only those associated with the user)
 *   - Rental types: no access
 *   - Articles: read-only (to be confirmed)
 * - **Prestazioni**
 *   - Offers: read/write (only those created by or associated with the user; cannot finalize offers)
 *   - Orders: read/write
 * - **Administration**: (to be confirmed)
 * - **Cleaning**: read/write
 * - **Dashboard**: only COCA has access
 *
 * ## Functions
 * - `hasPermission(allowedUserTypes: string[]): boolean`: Checks if the current user has one of the allowed user types.
 * - `canAccessCountry(country: string): boolean`: Checks if the current user can access the specified country.
 * - `canViewInvitesTab(): boolean`: Checks if the current user can view the invites tab (PRAD only).
 * - `hasUserType(requiredUserType: string): boolean`: Checks if the current user has the specified user type.
 * - `isDealer(): boolean`: Checks if the current user is a dealer (COCA or COOP).
 * - `isAdmin(): boolean`: Checks if the current user is an admin (PRAD, ADAM, or ADCO).
 * - `canApproveOffers(): boolean`: Checks if the current user can approve offers.
 * - `canFinalizeOffers(): boolean`: Checks if the current user can finalize offers.
 * - `canCreateOffers(): boolean`: Checks if the current user can create offers.
 * - `canSendOffersForVerification(): boolean`: Checks if the current user can send offers for verification.
 *
 * @remarks
 * This composable is intended to centralize permission logic for the frontend, making it easier to manage and update access rules as business requirements evolve.
 */

import type { UserType, UserTypeObject } from "~/interfaces/user";
import { useAuthStore } from "~/store/auth";

export function usePermissions() {
  const authStore = useAuthStore();

  const isUserTypeObject = (u: unknown): u is UserTypeObject => {
    return (
      typeof u === "object" &&
      u !== null &&
      "value" in (u as object) &&
      typeof (u as { value: unknown }).value === "string"
    );
  };

  const getUserType = (): UserType | undefined => {
    const userType = authStore.user?.user_type;
    if (!userType) return undefined;
    if (typeof userType === "string") return userType as UserType;
    if (isUserTypeObject(userType)) return userType.value;
    return undefined;
  };

  const hasPermission = (allowedUserTypes: string[]): boolean => {
    const currentUserType = getUserType();
    return (
      currentUserType !== undefined &&
      allowedUserTypes.includes(currentUserType)
    );
  };

  const canAccessCountry = (country: string): boolean => {
    return authStore.canAccessCountry(country);
  };

  const canViewInvitesTab = (): boolean => {
    const requiredUserTypes = ["PRAD"];
    return hasPermission(requiredUserTypes);
  };

  const canViewUsersTab = (): boolean => {
    const requiredUserTypes = ["PRAD"];
    return hasPermission(requiredUserTypes);
  };

  const hasUserType = (requiredUserType: string): boolean => {
    const currentUserType = getUserType();

    return currentUserType === requiredUserType;
  };

  const isDealer = (): boolean => {
    return hasPermission(["COCA", "COOP"]);
  };

  const isAdmin = (): boolean => {
    return hasPermission(["PRAD", "ADAM", "ADCO"]);
  };

  const canApproveOffers = (): boolean => {
    return hasPermission(["PRAD", "ADAM", "ADCO"]);
  };

  const canFinalizeOffers = (): boolean => {
    return hasPermission(["PRAD", "ADAM", "ADCO"]);
  };

  const canCreateOffers = (): boolean => {
    return hasPermission(["PRAD", "ADAM", "ADCO", "COCA", "COOP"]);
  };

  const canSendOffersForVerification = (): boolean => {
    return hasPermission(["COCA", "COOP"]);
  };

  const canEditOffers = (): boolean => {
    return hasPermission(["PRAD", "ADAM", "ADCO", "COCA", "COOP"]);
  };

  const canViewAllOffers = (): boolean => {
    return hasPermission(["PRAD", "ADAM", "ADCO"]);
  };

  const canGenerateContracts = (): boolean => {
    return hasPermission(["PRAD", "ADAM", "ADCO", "COCA", "COOP"]);
  };

  const canUploadSignedContracts = (): boolean => {
    // Tutti possono caricare contratti firmati
    return hasPermission(["PRAD", "ADAM", "ADCO", "COCA", "COOP"]);
  };

  // Menu-specific permissions
  const canAccessDashboard = (): boolean => {
    // Dashboard: PRAD (full), ADAM/ADCO (read-only), COCA (access), COOP (no access)
    return hasPermission(["PRAD", "ADAM", "ADCO", "COCA"]);
  };

  const canAccessDealers = (): boolean => {
    // Dealers: PRAD, ADAM, ADCO (read/write), COCA/COOP (no access)
    return hasPermission(["PRAD", "ADAM", "ADCO"]);
  };

  const canAccessRentalTypes = (): boolean => {
    // Rental Types: PRAD, ADAM, ADCO (read-only), COCA/COOP (no access)
    return hasPermission(["PRAD", "ADAM", "ADCO"]);
  };

  const canAccessClients = (): boolean => {
    // Clients: tutti hanno accesso con limitazioni diverse
    return hasPermission(["PRAD", "ADAM", "ADCO", "COCA", "COOP"]);
  };

  const canAccessPriceLists = (): boolean => {
    // Price Lists: tutti hanno accesso (read-only per molti)
    return hasPermission(["PRAD", "ADAM", "ADCO", "COCA", "COOP"]);
  };

  const canAccessArticles = (): boolean => {
    // Articles: tutti hanno accesso
    return hasPermission(["PRAD", "ADAM", "ADCO", "COCA", "COOP"]);
  };

  const canAccessMarketing = (): boolean => {
    return hasPermission(["PRAD", "ADAM", "ADCO"]);
  };

  const canChangeProspectStatus = (): boolean => {
    return hasPermission(["PRAD", "ADAM", "ADCO"]);
  };

  const canViewSearchbarFilters = (): boolean => {
    return hasPermission(["PRAD", "ADAM", "ADCO"]);
  };

  const canEditClients = (): boolean => {
    return hasPermission(["PRAD", "ADAM", "ADCO"]);
  };

  const canEditThisClient = (clientHasOrders: boolean): boolean => {
    if (isAdmin()) {
      return true;
    }
    if (isDealer()) {
      return !clientHasOrders;
    }
    return false;
  };

  const canCreateInvoices = (): boolean => {
    return hasPermission(["PRAD", "ADAM"]);
  };

  const canCreateCN = (): boolean => {
    return hasPermission(["PRAD", "ADAM"]);
  };

  const canRemoveCatalogPrices = (_targetDealerId?: number): boolean => {
    // Only admin user types can remove catalog prices from dealers.
    // Prevent dealer/operator user types (COCA/COOP) from removing their own price lists.
    return hasPermission(["PRAD", "ADAM", "ADCO"]);
  };

  const canEditAddressManually = (): boolean => {
    // Manual address entry (bypassing Google Maps autocomplete) is only available to admin users.
    return isAdmin();
  };

  return {
    hasPermission,
    canAccessCountry,
    canViewInvitesTab,
    canViewUsersTab,
    hasUserType,
    isDealer,
    isAdmin,
    canApproveOffers,
    canFinalizeOffers,
    canCreateOffers,
    canSendOffersForVerification,
    canEditOffers,
    canViewAllOffers,
    canGenerateContracts,
    canUploadSignedContracts,
    canAccessMarketing,
    canChangeProspectStatus,
    canViewSearchbarFilters,
    canEditClients,
    canEditThisClient,
    canCreateInvoices,
    canAccessDashboard,
    canAccessDealers,
    canAccessRentalTypes,
    canAccessClients,
    canAccessPriceLists,
    canAccessArticles,
    canCreateCN,
    canRemoveCatalogPrices,
    canEditAddressManually,
  };
}
